import { Directive, inject, InjectionToken, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { of, switchMap } from 'rxjs';
import { FormComponent } from './form.component';

/*
 * Projektovaný formulář: komponenta (hostitel) má ve své šabloně `ui-form` a do něj promítá pole zvenku
 * (`<ng-content>`). Promítnutá pole se ale formuláře ptají podle místa deklarace – injektor jde přes hostitele,
 * ne přes vnitřní `ui-form` –, takže by `formControlName` ani stav odeslání (zobrazení chyb) nenašla.
 *
 * Hostitel proto nese:
 * - `ProjectedFormGroupDirective` jako host direktivu (`FormGroupDirective` jako `ControlContainer` pro
 *   `formControlName` promítnutých polí i pro vnitřní `ui-form`, který tak `[formGroup]` nepotřebuje),
 * - `PROJECTED_FORM_HOST` (sám sebe, se signálem vnitřního `ui-form`) a zástupce `FormComponent`
 *   z `projectedFormProxy()`, přes kterého se pole ptají na stav odeslání.
 *
 * @example
 * @Component({
 *   selector: 'ui-muj-formular',
 *   template: `<ui-form (handleSubmit)="…"><ng-content /></ui-form>`,
 *   hostDirectives: [{ directive: ProjectedFormGroupDirective, inputs: ['formGroup: form'] }],
 *   providers: [
 *     { provide: PROJECTED_FORM_HOST, useExisting: forwardRef(() => MujFormularComponent) },
 *     { provide: FormComponent, useFactory: projectedFormProxy },
 *   ],
 * })
 * export class MujFormularComponent implements ProjectedFormHost {
 *   readonly formComponent = viewChild(FormComponent);
 * }
 */

/**
 * `FormGroupDirective` použitelná jako host direktiva (`FormGroupDirective` není standalone): hostitel
 * projektovaného formuláře ji nese, aby `formControlName` v promítnutém obsahu i vnitřní `ui-form` našly formulář.
 */
@Directive({
  selector: '[uiProjectedFormGroup]',
  providers: [{ provide: ControlContainer, useExisting: ProjectedFormGroupDirective }],
})
export class ProjectedFormGroupDirective extends FormGroupDirective {}

/** Hostitel projektovaného formuláře: vnitřní `ui-form` (signal query `viewChild(FormComponent)`). */
export interface ProjectedFormHost {
  readonly formComponent: Signal<FormComponent | undefined>;
}

export const PROJECTED_FORM_HOST = new InjectionToken<ProjectedFormHost>('PROJECTED_FORM_HOST');

/**
 * Zástupce `FormComponent` pro promítnutá pole (`{ provide: FormComponent, useFactory: projectedFormProxy }`):
 * stav odeslání (`getSubmitted()`, `submitted`) čte z vnitřního `ui-form` hostitele (`PROJECTED_FORM_HOST`);
 * do vytvoření pohledu hostitele hlásí neodeslaný formulář.
 */
export function projectedFormProxy(): Pick<FormComponent, 'getSubmitted' | 'submitted'> {
  const host = inject(PROJECTED_FORM_HOST);
  const submitted$ = toObservable(host.formComponent).pipe(switchMap((form) => form?.getSubmitted() ?? of(false)));
  return {
    getSubmitted: () => submitted$,
    get submitted() {
      return host.formComponent()?.submitted ?? false;
    },
  };
}
